import { useQuery } from '@tanstack/react-query';

import { relatedProducts } from '@Api/RelatedProducts';
import { useCartShortInfo } from '@Queries/Cart';
import generateProductHash from '@Utils/generateProductHash';

import type { IApiRelatedProducts, IRelatedProducts } from '@Types/RelatedProducts';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ProductData } from '@Types/Product';
import type { Tab } from '@divlab/divanui';

const transformRelatedProducts = (data: IApiRelatedProducts) =>
  data.flatMap((item) =>
    item.lists.flatMap((list) => list.products.map((product) => ({ ...product, quantity: 0 }))),
  );

const useRelatedProducts = (id: number) => {
  const key = ['related-products', id];

  const query = useQuery<IRelatedProducts>({
    queryKey: key,
    queryFn: async () => {
      const response = await relatedProducts.getAll(id);
      let tabs: Tab<string>[] = [];
      const products: ProductData[] = transformRelatedProducts(response);

      const stringTabs = Array.from(
        new Set(products.map((product) => [product.typeId, product.type].join('/'))),
      );

      tabs = stringTabs.map((tab) => {
        const [typeId, type] = tab.split('/');

        return { id: typeId, label: type, data: type };
      });

      return {
        kitTitle: response[0].lists[0].title,
        products,
        changedProducts: products,
        tabs,
      };
    },
  });

  return query;
};

const sortRelatedProducts = (products: ProductData[], hashes: string[]) => {
  if (!hashes?.length) return products;

  return [...products]
    .map((product) => ({
      ...product,
      isAdded: hashes.includes(generateProductHash({ shopProductId: product.id })),
    }))
    .sort((prev, current) => {
      const hasPrevInCart = hashes.includes(generateProductHash({ shopProductId: prev.id }));
      const hasCurrentInCart = hashes.includes(generateProductHash({ shopProductId: current.id }));

      if (hasPrevInCart && !hasCurrentInCart) return -1;

      return 1;
    });
};

const useSortedRelatedProducts = (
  id: number,
  notSortByCart?: boolean,
): UseQueryResult<IRelatedProducts, Error> => {
  const relatedProductsData = useRelatedProducts(id);
  const { data: allRelatedProducts, ...restProps } = relatedProductsData;
  const { data: shortCard } = useCartShortInfo();
  const { products } = allRelatedProducts || {};

  if (!allRelatedProducts) return relatedProductsData;

  const sortedProducts = notSortByCart
    ? products
    : sortRelatedProducts(products, shortCard?.positions);

  return {
    ...restProps,
    data: { ...allRelatedProducts, products: sortedProducts },
  } as UseQueryResult<IRelatedProducts, Error>;
};

export default useSortedRelatedProducts;
